import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJobStore } from "../../../store/jobStore";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { NarrativeTemplate } from "../../../shared/interfaces/ReportGenReport";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { jobsClient } from "@/shared/api/clients";
import { jobStartHitlToolRegistry } from "../../../components/chat/hitl/registry";
import { useHitlEnabled } from "@/shared/hooks/useHitlEnabled";
import { resolveNarrativeTemplateId } from "../builtInTemplates";

export const useReportGeneration = (
  evidenceID: string | undefined,
  reportType: NarrativeTemplate,
  videoDataList: VideoData[],
  isMaxJobsReached: boolean,
  showError: (message: string) => void,
  /** Free-text details the officer wants the agent to know while writing. */
  additionalContext?: string
) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const addJob = useJobStore((state) => state.addJob);
  const navigate = useNavigate();
  const hitlEnabled = useHitlEnabled();

  const handleGenerateReport = useCallback(() => {
    if (isGenerating) return;
    if (isMaxJobsReached) {
      showError("Maximum number of concurrent jobs reached. Please wait for existing jobs to complete.");
      return;
    }
    setIsGenerating(true);

    // `undefined` for Free Form Narrative — axios drops the keys, so the job
    // starts template-less rather than referencing an id no station row has.
    const narrativeTemplateId = resolveNarrativeTemplateId(reportType);

    const trimmedContext = additionalContext?.trim();

    const requestBody: Record<string, unknown> = {
      reportType: narrativeTemplateId,
      // The field-selection UI was removed as dead code; the API still expects the key.
      selectedFields: [],
      videos: evidenceID?.split("~"),
      narrativeTemplateId,
      // Omitted entirely when blank so the job runs with no extra instructions.
      ...(trimmedContext ? { additionalContext: trimmedContext } : {}),
      ...(hitlEnabled ? { hitlToolRegistry: jobStartHitlToolRegistry } : {}),
    };

    const evidenceIDs = evidenceID?.split("~") || [];
    jobsClient
      .post("/jobs", requestBody)
      .then((res) => {
        addJob({
          id: res.data.jobId,
          status: ReportJobStatus.PENDING,
          progress: 0,
          evidenceIDs,
          selectedFields: [],
          videoTitles: videoDataList.map((video) => video.title),
          // Initial generation produces a narrative-only report (the full
          // report is an explicit follow-up expansion in the editor), so the
          // tracker shows a 2-minute countdown instead of a percentage.
          narrativeOnly: true,
        });
        // Report is configured and queued; return to My Reports, where the
        // Report Tracker shows it processing. (The /generating/:jobId surface
        // is still reachable from the tracker for live progress.)
        navigate("/");
      })
      .catch((error) => {
        showError(
          error.response?.data?.error ||
            "An error occurred while generating the report. Please try again later."
        );
      })
      .finally(() => setIsGenerating(false));
  }, [isGenerating, isMaxJobsReached, reportType, evidenceID, addJob, videoDataList, navigate, showError, hitlEnabled, additionalContext]);

  const handleSubmitClick = useCallback(() => {
    handleGenerateReport();
  }, [handleGenerateReport]);

  return { isGenerating, handleSubmitClick };
};
